(function () {
  /**
   * @typedef {{ destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void }} LiveEventsSwiperInstance
   */

  /**
   * @typedef {{ new (el: Element, options?: object): LiveEventsSwiperInstance }} LiveEventsSwiperConstructor
   */

  /**
   * @param {(Element & { swiper?: LiveEventsSwiperInstance }) | null | undefined} el
   */
  function destroySlider(el) {
    if (el && el.swiper) {
      el.swiper.destroy(true, true);
    }
    if (el) {
      el.classList.remove('live-events-slider--ready', 'live-events-slider--single');
    }
  }

  /**
   * @param {HTMLElement} sectionEl
   */
  function initSection(sectionEl) {
    var sliderCandidate = sectionEl.querySelector('.live-events-header [data-le="slider"]');
    if (!(sliderCandidate instanceof HTMLElement)) {
      return;
    }
    var slider = sliderCandidate;
    if (slider.classList.contains('live-events-slider--ready')) {
      return;
    }

    var slides = slider.querySelectorAll('.swiper-slide');
    if (!slides.length) {
      return;
    }

    var win = /** @type {Window & { Swiper?: LiveEventsSwiperConstructor }} */ (window);
    var SwiperCtor = win.Swiper;
    if (!SwiperCtor) {
      return;
    }

    if (slides.length <= 1) {
      slider.classList.add('live-events-slider--single', 'live-events-slider--ready');
      return;
    }

    var sid = sectionEl.id;
    if (!sid) {
      return;
    }

    var showArrows = slider.dataset.arrows !== 'false';
    var showDots = slider.dataset.dots !== 'false';
    var slidesMd = parseFloat(slider.dataset.slidesMd || '1.15') || 1;
    var slidesLg = parseFloat(slider.dataset.slidesLg || '1.25') || 1;

    /** @type {Record<string, unknown>} */
    var opts = {
      slidesPerView: 1,
      /* Gap comes from CSS --le-slide-gap on the slider (matches theme section spacing). */
      spaceBetween: 0,
      watchOverflow: true,
      keyboard: true,
      breakpoints: {
        750: { slidesPerView: slidesMd },
        990: { slidesPerView: slidesLg },
      },
    };

    if (showArrows) {
      opts.navigation = {
        nextEl: '#' + sid + ' .swiper-button-next',
        prevEl: '#' + sid + ' .swiper-button-prev',
      };
    }
    if (showDots) {
      opts.pagination = {
        el: '#' + sid + ' .swiper-pagination',
        clickable: true,
      };
    }

    new SwiperCtor(slider, opts);
    slider.classList.add('live-events-slider--ready');
  }

  /**
   * @param {HTMLElement} drawer
   */
  function setDrawerHeightOpen(drawer) {
    // Temporarily clear max-height so we can measure natural height.
    drawer.style.maxHeight = 'none';
    var h = drawer.scrollHeight;
    drawer.style.maxHeight = h + 'px';
  }

  /**
   * @param {HTMLElement} drawer
   */
  function closeDrawer(drawer) {
    drawer.style.maxHeight = '0px';
  }

  /**
   * @param {HTMLElement} hit
   * @param {HTMLElement} barRow
   * @param {HTMLElement} drawer
   * @param {HTMLElement} sectionEl
   * @param {boolean} forceOpen
   */
  function openDrawer(hit, barRow, drawer, sectionEl, forceOpen) {
    hit.classList.add('is-open');
    barRow.setAttribute('aria-expanded', 'true');
    // Slide open: set measured height so CSS max-height transition runs.
    setDrawerHeightOpen(drawer);
    // Swiper inside a hidden container needs init/update after opening.
    initSection(sectionEl);
    var sliderEl = drawer.querySelector('[data-le="slider"]');
    if (sliderEl && /** @type {any} */ (sliderEl).swiper && /** @type {any} */ (sliderEl).swiper.update) {
      /** @type {any} */ (sliderEl).swiper.update();
    }
  }

  /**
   * @param {HTMLElement} sectionEl
   */
  function bindToggle(sectionEl) {
    if (sectionEl.dataset.leHeaderBound === 'true') {
      return;
    }

    var root = sectionEl.querySelector('.live-events-header');
    if (!(root instanceof HTMLElement)) return;

    /** @type {HTMLElement | null} */
    var hit = /** @type {any} */ (root.querySelector('[data-le="hit"]'));
    /** @type {HTMLElement | null} */
    var barRow = /** @type {any} */ (root.querySelector('[data-le="bar-row"]'));
    /** @type {HTMLElement | null} */
    var drawer = /** @type {any} */ (root.querySelector('[data-le="drawer"]'));

    if (!hit || !barRow || !drawer) {
      return;
    }

    /** @type {HTMLElement} */
    var hitEl = hit;
    /** @type {HTMLElement} */
    var barRowEl = barRow;
    /** @type {HTMLElement} */
    var drawerEl = drawer;

    // Ensure closed state is explicit for max-height animation.
    drawerEl.style.maxHeight = '0px';

    function doToggle() {
      var isOpen = hitEl.classList.contains('is-open');
      if (isOpen) {
        hitEl.classList.remove('is-open');
        barRowEl.setAttribute('aria-expanded', 'false');
        closeDrawer(drawerEl);
        return;
      }
      openDrawer(hitEl, barRowEl, drawerEl, sectionEl, true);
    }

    barRowEl.addEventListener('click', function (e) {
      e.preventDefault();
      doToggle();
    });

    barRowEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        doToggle();
      }
    });

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof Node)) return;
      if (!hitEl.classList.contains('is-open')) return;
      if (hitEl.contains(t)) return;
      hitEl.classList.remove('is-open');
      barRowEl.setAttribute('aria-expanded', 'false');
      closeDrawer(drawerEl);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!hitEl.classList.contains('is-open')) return;
      hitEl.classList.remove('is-open');
      barRowEl.setAttribute('aria-expanded', 'false');
      closeDrawer(drawerEl);
    });

    window.addEventListener('resize', function () {
      if (!hitEl.classList.contains('is-open')) return;
      setDrawerHeightOpen(drawerEl);
      var sliderEl = drawerEl.querySelector('[data-le="slider"]');
      // @ts-ignore
      if (sliderEl && sliderEl.swiper && sliderEl.swiper.update) {
        // @ts-ignore
        sliderEl.swiper.update();
      }
    });

    sectionEl.dataset.leHeaderBound = 'true';
  }

  function initAll() {
    document.querySelectorAll('.shopify-section').forEach(function (sec) {
      if (sec instanceof HTMLElement && sec.querySelector('.live-events-header')) {
        bindToggle(sec);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    if (target.querySelector('.live-events-header')) {
      bindToggle(target);
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var target = e.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    var s = target.querySelector('[data-le="slider"]');
    destroySlider(s);
  });
})();
